import {
  useMemo,
  useCallback,
  useRef,
  useEffect,
  type ReactNode,
  ComponentProps,
  Dispatch,
  SetStateAction,
} from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Button } from "./button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./command";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { cn } from "../lib/utils";
import { useDebouncedValue, useUncontrolledState } from "../hooks";

/**
 * Base interface for combobox options
 */
export interface ComboboxOption<T = unknown> {
  /** Unique identifier for the option */
  value: string;
  /** Primary display text */
  label: string;
  /** Optional secondary text (shown below label) */
  description?: string;
  /** Whether this option is disabled */
  disabled?: boolean;
  /** Additional metadata for custom rendering */
  metadata?: Record<string, T>;
}

/**
 * Props for rendering a custom option
 */
export interface ComboboxOptionRenderProps<T> {
  option: ComboboxOption<T>;
  isSelected: boolean;
}

/**
 * Props for the Combobox component
 */
export interface ComboboxProps<T, Multiple extends boolean = false>
  extends Omit<ComponentProps<typeof Popover>, "open" | "onOpenChange"> {
  /** Current selected value(s) */
  value: (Multiple extends false ? ComboboxOption<T> : ComboboxOption<T>[]) | undefined;
  /** Callback when selection changes */
  onChange: (value: Multiple extends false ? ComboboxOption<T> | undefined : ComboboxOption<T>[]) => void;
  /** Enable multi-select mode */
  multiple?: Multiple;

  /** Static options list */
  options?: ComboboxOption<T>[];
  /** When true, the customFilter function will always be run instead of skipping if search is empty */
  alwaysFilter?: boolean;
  /** Custom filter function for search */
  customFilter?: (searchTerm: string, options: ComboboxOption<T>[]) => ComboboxOption<T>[];

  /** Placeholder text for trigger button */
  placeholder?: string;
  /** Placeholder text for search input */
  searchPlaceholder?: string;
  /** Custom function to format selected value(s) in trigger */
  formatSelectedValue?: (value: Multiple extends false ? ComboboxOption<T> : ComboboxOption<T>[]) => ReactNode;
  /** Custom function to render each option */
  formatOption?: (props: ComboboxOptionRenderProps<T>) => ReactNode;

  /** Loading state */
  isLoading?: boolean;
  /** Text to show when loading */
  loadingText?: string;
  /** Text to show when no results found */
  emptyText?: string;

  /** Close popover on selection (default: true for single-select, false for multi-select) */
  closeOnSelect?: boolean;
  /** Show clear button when value is selected */
  clearable?: boolean;

  /** CSS class for trigger button wrapper */
  className?: string;
  /** CSS class for popover content */
  popoverClassName?: string;

  /** Accessibility label */
  "aria-label"?: string;

  /** Callback when user scrolls near the bottom (for infinite loading) */
  onLoadMore?: () => void;
  /** Whether there are more items to load */
  hasMore?: boolean;
  /** Whether currently loading more items */
  isLoadingMore?: boolean;
  searchQuery?: string;
  setSearchQuery?: Dispatch<SetStateAction<string>>;
  children?: ReactNode;
  open?: boolean;
  setOpen?: Dispatch<SetStateAction<boolean>>;
}

/**
 * Default filter function that searches label and description
 */
function defaultFilter<T>(searchTerm: string, options: ComboboxOption<T>[]): ComboboxOption<T>[] {
  const lowerSearch = searchTerm.toLowerCase();
  return options.filter(
    (option) =>
      option.label.toLowerCase().includes(lowerSearch) ||
      option.description?.toLowerCase().includes(lowerSearch) ||
      option.value.toLowerCase().includes(lowerSearch)
  );
}

/**
 * Default selected value formatter
 */
function defaultFormatSelectedValue<T>(value: ComboboxOption<T> | ComboboxOption<T>[]): ReactNode {
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    return (
      <div className="flex flex-col gap-1 items-start overflow-hidden w-full text-left">
        <div className="font-medium text-sm truncate w-full">{value.map((v) => v.label).join(", ")}</div>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1 items-start overflow-hidden w-full text-left">
      <div className="font-medium text-sm truncate w-full">{value.label}</div>
    </div>
  );
}

/**
 * Default option renderer
 */
function defaultFormatOption<T>({ option, isSelected }: ComboboxOptionRenderProps<T>): ReactNode {
  return (
    <>
      <div className="flex flex-col gap-1 w-full">
        <div className="font-medium text-sm truncate">{option.label}</div>
        {option.description && <div className="text-xs text-muted-foreground truncate">{option.description}</div>}
      </div>
      <Check className={cn("ml-auto h-4 w-4 shrink-0", isSelected ? "opacity-100" : "opacity-0")} />
    </>
  );
}

/**
 * Reusable Combobox component combining Popover and Command
 *
 * Supports both single and multi-select modes with search, loading states,
 * async data loading, and custom rendering.
 *
 * @example
 * // Single-select
 * <Combobox
 *   value={selectedOption}
 *   onChange={setSelectedOption}
 *   options={options}
 *   placeholder="Select an option..."
 * />
 *
 * @example
 * // Multi-select with descriptions
 * <Combobox
 *   multiple
 *   value={selectedOptions}
 *   onChange={setSelectedOptions}
 *   options={options}
 *   placeholder="Select options..."
 * />
 *
 * @example
 * // With async loading
 * <Combobox
 *   value={selected}
 *   onChange={setSelected}
 *   options={data}
 *   isLoading={isLoading}
 *   onLoadMore={fetchMore}
 *   hasMore={hasNextPage}
 * />
 */
export function Combobox<T, Multiple extends boolean = false>({
  value,
  onChange,
  multiple: providedMultiple,
  options = [],
  alwaysFilter,
  customFilter,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  formatSelectedValue = defaultFormatSelectedValue,
  formatOption = defaultFormatOption,
  isLoading = false,
  loadingText = "Loading...",
  emptyText = "No results found",
  closeOnSelect,
  clearable = false,
  className,
  popoverClassName,
  "aria-label": ariaLabel,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  searchQuery: providedSearchQuery,
  setSearchQuery: providedSetSearchQuery,
  children,
  defaultOpen,
  open: providedOpen,
  setOpen: providedSetOpen,
  ...rest
}: ComboboxProps<T, Multiple>) {
  const multiple = providedMultiple ?? false;
  type Option = Multiple extends false ? ComboboxOption<T> : ComboboxOption<T>[];
  const [open, setOpen] = useUncontrolledState({
    state: providedOpen ?? defaultOpen ?? false,
    setState: providedSetOpen,
  });
  const [searchQuery, setSearchQuery] = useUncontrolledState({
    state: providedSearchQuery ?? "",
    setState: providedSetSearchQuery,
  });
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Determine closeOnSelect default based on multiple prop
  const shouldCloseOnSelect = closeOnSelect ?? !multiple;

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    if (!debouncedSearchQuery && !alwaysFilter) return options;
    const filterFn = customFilter || defaultFilter;
    return filterFn(debouncedSearchQuery, options);
  }, [options, debouncedSearchQuery, customFilter, alwaysFilter]);

  // Check if an option is selected
  const isOptionSelected = useCallback(
    (option: ComboboxOption<T>): boolean => {
      if (!value) return false;
      if (Array.isArray(value)) {
        return value.some((v) => v.value === option.value);
      }
      return value.value === option.value;
    },
    [value]
  );

  // Handle option selection
  const handleSelect = useCallback(
    (option: ComboboxOption<T>) => {
      if (option.disabled) return;

      if (multiple) {
        const valueArray = (value as ComboboxOption<T>[] | undefined) ?? [];
        const isSelected = valueArray.some((v) => v.value === option.value);

        if (isSelected) {
          onChange(valueArray.filter((v) => v.value !== option.value) as Option);
        } else {
          onChange([...valueArray, option] as Option);
        }
      } else {
        onChange(option as Option);
        if (shouldCloseOnSelect) {
          setOpen(false);
        }
      }
    },
    [multiple, value, onChange, shouldCloseOnSelect, setOpen]
  );

  // Handle clear button
  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const clearValue = (multiple ? [] : undefined) as unknown as Option;
      onChange(clearValue);
    },
    [onChange, multiple]
  );

  // Check if value is empty
  const hasValue = useMemo(() => {
    if (Array.isArray(value)) return value.length > 0;
    if (!value?.value) return false;
    return true;
  }, [value]);

  // Infinite scroll detection - attach to the popover content when it opens
  useEffect(() => {
    if (!open || !onLoadMore || !hasMore || isLoadingMore) return;

    // Find the scrollable container within CommandList
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      // Trigger when scrolled within 100px of bottom
      if (scrollHeight - scrollTop - clientHeight < 100) {
        onLoadMore();
      }
    };

    scrollContainer.addEventListener("scroll", handleScroll);
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, [open, onLoadMore, hasMore, isLoadingMore]);

  return (
    <Popover open={open} onOpenChange={setOpen} {...rest}>
      <div className={cn("flex w-full items-center gap-1", className)}>
        <PopoverTrigger asChild className="flex-1 min-w-0">
          {children ?? (
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              aria-label={ariaLabel}
              className="w-full justify-between font-normal h-8 py-2"
            >
              {hasValue ? formatSelectedValue(value!) : <span className="text-muted-foreground">{placeholder}</span>}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          )}
        </PopoverTrigger>
        {clearable && hasValue && (
          <Button variant="ghost" size="sm-icon" onClick={handleClear} aria-label="Clear selection">
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
      <PopoverContent className={cn("w-[var(--radix-popover-trigger-width)] p-0", popoverClassName)}>
        <Command shouldFilter={false}>
          <CommandInput
            value={searchQuery}
            onValueChange={setSearchQuery}
            placeholder={searchPlaceholder}
            className="h-9"
          />
          <div ref={scrollContainerRef} onWheelCapture={(e) => e.stopPropagation()}>
            <CommandList>
              <CommandEmpty>{isLoading ? loadingText : emptyText}</CommandEmpty>
              <CommandGroup>
                {filteredOptions.map((option) => {
                  const isSelected = isOptionSelected(option);
                  return (
                    <CommandItem
                      key={`${option.value}-${option.label}`}
                      value={option.value}
                      onSelect={() => handleSelect(option)}
                      disabled={option.disabled}
                    >
                      {formatOption({ option, isSelected })}
                    </CommandItem>
                  );
                })}
                {isLoadingMore && (
                  <CommandItem disabled>
                    <div className="text-sm text-muted-foreground">Loading more...</div>
                  </CommandItem>
                )}
              </CommandGroup>
            </CommandList>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
